import { useState, useEffect, useRef, useMemo } from 'react'
import { RackMeta, DcMeta, rackLabel } from '../../hooks/useCalculatorLogic'

export function RackPicker({ label, racks, value, onChange, excludeId, dc }: {
  label: string; racks: RackMeta[]; value: RackMeta | null
  onChange: (r: RackMeta) => void; excludeId?: number; dc?: DcMeta
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  const items = useMemo(() =>
    racks
      .filter(r => r.id !== excludeId && rackLabel(r, dc).toLowerCase().includes(q.toLowerCase()))
      .slice(0, 80),
    [racks, excludeId, q, dc]
  )

  return (
    <div ref={rootRef} className="relative">
      <label className="block text-[11px] text-text-muted mb-1">{label}</label>
      <button
        type="button"
        onClick={() => { setOpen(o => !o); setQ('') }}
        className="w-full flex items-center justify-between bg-bg-input border border-border rounded px-2 py-1.5 text-xs text-text-primary hover:border-border-focus transition-colors"
      >
        <span className="truncate font-mono text-left">
          {value ? rackLabel(value, dc) : '— выберите —'}
        </span>
        <span className="text-text-muted ml-2 shrink-0 text-[10px]">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="absolute top-full mt-1 left-0 right-0 z-50 bg-bg-panel border border-border rounded shadow-lg overflow-hidden">
          <div className="p-1.5">
            <input
              autoFocus
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Поиск..."
              className="w-full bg-bg-input border border-border rounded px-2 py-1 text-xs text-text-primary outline-none focus:border-border-focus"
            />
          </div>
          <div className="max-h-44 overflow-y-auto">
            {items.length === 0
              ? <div className="px-3 py-2 text-xs text-text-muted">Нет совпадений</div>
              : items.map(r => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => { onChange(r); setOpen(false); setQ('') }}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs font-mono transition-colors
                    ${value?.id === r.id ? 'bg-bg-active text-text-primary' : 'text-text-secondary hover:bg-bg-hover'}`}
                >
                  <span className="text-text-muted shrink-0 w-8 tabular-nums">R{r.row_index + 1}</span>
                  {rackLabel(r, dc)}
                </button>
              ))
            }
          </div>
        </div>
      )}
    </div>
  )
}
